/**
 * Cross-room recordings archive client (GET /api/v1/recordings, docs/contract.md M6): the
 * control service answers with the recordings of every room where the caller's Keycloak
 * identity is owner/admin/moderator (participants get 403), newest first, capped at 200.
 * Authentication is the SPA's SSO session bearer - the same token the calendar calls carry.
 * Downloads reuse the room-scoped route, which also accepts the SSO bearer for admins and
 * moderators (fetchRecordingFile in ../api/recording).
 */

import { RecordingApiError, parseRecordingFile, isActiveRecordingStatus } from '../api/recording';
import type { RecordingFile, RecordingFilePurpose, RecordingStatus } from '../api/recording';

/** One archived recording with the room it belongs to (listRecordingJSON on the wire). */
export interface RoomRecording {
  readonly id: number;
  readonly roomSlug: string;
  readonly status: RecordingStatus;
  /** RFC 3339 timestamp of the recording start. */
  readonly startedAt: string;
  /** RFC 3339 timestamp once the recording reached a terminal state; null before. */
  readonly stoppedAt: string | null;
  /** Worker failure detail once the recording failed; null otherwise. */
  readonly error: string | null;
  readonly files: readonly RecordingFile[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Narrows one archive row. id/roomSlug/status/startedAt are required; rows violating the
 * vocabulary yield null and are skipped by the list parser (drop, don't guess), mirroring the
 * room-scoped list parser. `files` defaults to empty (a recording mid-start has none);
 * `error` and `stoppedAt` accept null or a non-empty string.
 */
export function parseRoomRecording(value: unknown): RoomRecording | null {
  if (!isRecord(value)) {
    return null;
  }
  const id =
    typeof value.id === 'number' && Number.isInteger(value.id) && value.id >= 0 ? value.id : null;
  const roomSlug =
    typeof value.roomSlug === 'string' && value.roomSlug.length > 0 ? value.roomSlug : null;
  const status =
    value.status === 'starting' ||
    value.status === 'recording' ||
    value.status === 'stopping' ||
    value.status === 'completed' ||
    value.status === 'failed'
      ? value.status
      : null;
  const startedAt =
    typeof value.startedAt === 'string' && value.startedAt.length > 0 ? value.startedAt : null;
  if (id === null || roomSlug === null || status === null || startedAt === null) {
    return null;
  }
  let stoppedAt: string | null = null;
  if (value.stoppedAt !== undefined && value.stoppedAt !== null) {
    if (typeof value.stoppedAt !== 'string' || value.stoppedAt.length === 0) {
      return null;
    }
    stoppedAt = value.stoppedAt;
  }
  let error: string | null = null;
  if (value.error !== undefined && value.error !== null) {
    if (typeof value.error !== 'string' || value.error.length === 0) {
      return null;
    }
    error = value.error;
  }
  let files: RecordingFile[] = [];
  if (value.files !== undefined) {
    if (!Array.isArray(value.files)) {
      return null;
    }
    for (const entry of value.files) {
      const file = parseRecordingFile(entry);
      if (file === null) {
        return null;
      }
      files.push(file);
    }
  }
  return { id, roomSlug, status, startedAt, stoppedAt, error, files };
}

/**
 * Narrows the archive response body ({recordings: [...]}). The top level is strict; alien rows
 * are skipped so one malformed entry never blanks the archive.
 */
export function parseRoomRecordings(body: unknown): readonly RoomRecording[] {
  if (!isRecord(body) || !Array.isArray(body.recordings)) {
    throw new TypeError('recordings response must carry a "recordings" array');
  }
  const recordings: RoomRecording[] = [];
  for (const entry of body.recordings) {
    const recording = parseRoomRecording(entry);
    if (recording !== null) {
      recordings.push(recording);
    }
  }
  return recordings;
}

/**
 * GET /api/v1/recordings with the SSO bearer: every recording of the rooms the identity
 * moderates, newest first per the server.
 */
export async function listAllRecordings(
  token: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<readonly RoomRecording[]> {
  let response: Response;
  try {
    response = await fetchImpl('/api/v1/recordings', {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new RecordingApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    let code = 'unknown_error';
    let message = `recordings request failed with HTTP ${String(response.status)}`;
    try {
      const body: unknown = await response.json();
      if (isRecord(body) && isRecord(body.error)) {
        if (typeof body.error.code === 'string') {
          code = body.error.code;
        }
        if (typeof body.error.message === 'string') {
          message = body.error.message;
        }
      }
    } catch {
      // non-JSON error body, keep the generic message
    }
    throw new RecordingApiError(response.status, code, message);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new RecordingApiError(response.status, 'invalid_response', 'response is not valid JSON');
  }
  try {
    return parseRoomRecordings(body);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed recordings response';
    throw new RecordingApiError(response.status, 'invalid_response', detail);
  }
}

/** True while the archive row still changes (polling continues). */
export function isActiveRoomRecording(status: RecordingStatus): boolean {
  return isActiveRecordingStatus(status);
}

/** The fixed purpose order the file lists follow (overview, audio, video, mp3, transcript, merged, summary). */
export const PURPOSE_ORDER: readonly RecordingFilePurpose[] = [
  'overview',
  'audio',
  'video',
  'mp3',
  'transcript',
  'merged',
  'summary',
];
